// src/BirthdaySearch.jsx

import React, { useMemo, useState } from "react";
import { API, apiJSON, getActiveBranch } from "./apiBase";

export default function BirthdaySearch() {
  const [date, setDate] = useState("");
  const [results, setResults] = useState([]);
  const [voucherLinks, setVoucherLinks] = useState({});
  const [rowProgress, setRowProgress] = useState({});
  const [loading, setLoading] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);
  const [waBusy, setWaBusy] = useState(false);
  const [waReport, setWaReport] = useState(null);
  const [notice, setNotice] = useState("");
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");

  const TEMPLATE_NAME = "bv_1";
  const LANG = "en";
  const DEFAULT_IMG =
    "https://gobatroun.com/wp-content/uploads/2024/06/Bamboo-logo.jpg";

  const C = {
    bg: "#0b1220",
    card: "#111827",
    card2: "#0f172a",
    border: "rgba(255,255,255,0.1)",
    text: "#e5e7eb",
    dim: "#94a3b8",
    green: "#10b981",
    red: "#ef4444",
    yellow: "#f59e0b",
    blue: "#2563eb",
  };

  const s = {
    wrap: {
      background: C.bg,
      color: C.text,
      minHeight: "100vh",
      padding: 16,
      fontFamily: "system-ui, Arial",
    },
    card: {
      background: C.card,
      border: `1px solid ${C.border}`,
      borderRadius: 14,
      padding: 14,
      marginBottom: 14,
    },
    input: {
      background: C.card2,
      border: `1px solid ${C.border}`,
      color: C.text,
      borderRadius: 10,
      padding: "10px 12px",
      minHeight: 42,
    },
    smallInput: {
      background: C.card2,
      border: `1px solid ${C.border}`,
      color: C.text,
      borderRadius: 10,
      padding: "10px 12px",
      minHeight: 42,
      width: 120,
    },
    btn: {
      background: "rgba(59,130,246,0.15)",
      border: "1px solid rgba(59,130,246,0.35)",
      color: C.text,
      borderRadius: 10,
      padding: "10px 12px",
      cursor: "pointer",
      minHeight: 42,
      fontWeight: 700,
    },
    btnGreen: {
      background: "rgba(16,185,129,0.18)",
      border: "1px solid rgba(16,185,129,0.4)",
      color: C.text,
      borderRadius: 10,
      padding: "10px 12px",
      cursor: "pointer",
      minHeight: 42,
      fontWeight: 800,
    },
    row: {
      display: "flex",
      flexWrap: "wrap",
      gap: 10,
      alignItems: "center",
    },
    tableWrap: {
      overflowX: "auto",
      border: "1px solid #d1d5db",
      borderRadius: 12,
      background: "#ffffff",
      maxHeight: "70vh",
    },
    table: {
      width: "100%",
      minWidth: 1150,
      borderCollapse: "collapse",
      color: "#000000",
      background: "#ffffff",
    },
    th: {
      background: "#f3f4f6",
      color: "#000000",
      textAlign: "left",
      padding: 10,
      borderBottom: "1px solid #d1d5db",
      position: "sticky",
      top: 0,
      zIndex: 2,
      fontWeight: 800,
      whiteSpace: "nowrap",
    },
    td: {
      padding: 10,
      borderBottom: "1px solid #e5e7eb",
      verticalAlign: "top",
      color: "#000000",
      background: "#ffffff",
    },
    tdAlt: {
      padding: 10,
      borderBottom: "1px solid #e5e7eb",
      verticalAlign: "top",
      color: "#000000",
      background: "#f9fafb",
    },
    selectedTd: {
      padding: 10,
      borderBottom: "1px solid #bfdbfe",
      verticalAlign: "top",
      color: "#000000",
      background: "#dbeafe",
    },
    linkText: {
      color: "#111827",
      wordBreak: "break-all",
      marginTop: 6,
      fontSize: 13,
    },
  };

  function apiUrl(p) {
    return String(p).startsWith("http")
      ? p
      : `${API}${p.startsWith("/") ? p : `/${p}`}`;
  }

  function digitsOnly(v) {
    return String(v || "").replace(/\D+/g, "");
  }

  function cleanName(v) {
    return String(v || "").trim() || "Guest";
  }

  function safeFileName(v) {
    return (
      String(v || "UNKNOWN")
        .trim()
        .replace(/[<>:"/\\|?*]/g, "_") || "UNKNOWN"
    );
  }

  function linkKey(c) {
    const name = safeFileName(c?.name || c?.fullname || "");
    const phone = digitsOnly(c?.phones || c?.phone || "");
    return `${date}|${name}|${phone}`;
  }

  function show(msg) {
    setNotice(msg);
    setTimeout(() => setNotice(""), 5000);
  }

  function setProgress(key, patch) {
    setRowProgress((m) => ({
      ...m,
      [key]: {
        ...(m[key] || {}),
        ...patch,
      },
    }));
  }

  async function safePostJSON(path, payload) {
    const res = await fetch(apiUrl(path), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });

    const text = await res.text();

    let data = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      data = { raw: text };
    }

    if (!res.ok) {
      const detail =
        data?.detail ||
        data?.message ||
        data?.error ||
        data?.raw ||
        `HTTP ${res.status}`;

      throw new Error(
        typeof detail === "string" ? detail : JSON.stringify(detail)
      );
    }

    return data;
  }

  function getSelectedRows() {
    if (!results.length) return [];

    let from = parseInt(rangeFrom || "1", 10);
    let to = parseInt(rangeTo || String(results.length), 10);

    if (Number.isNaN(from) || from < 1) from = 1;
    if (Number.isNaN(to) || to > results.length) to = results.length;

    if (from > to) return [];

    return results.slice(from - 1, to);
  }

  function isRowSelected(index) {
    let from = parseInt(rangeFrom || "1", 10);
    let to = parseInt(rangeTo || String(results.length), 10);

    if (Number.isNaN(from) || from < 1) from = 1;
    if (Number.isNaN(to) || to > results.length) to = results.length;

    const rowNumber = index + 1;
    return rowNumber >= from && rowNumber <= to;
  }

  const selectedRows = useMemo(
    () => getSelectedRows(),
    [results, rangeFrom, rangeTo]
  );

  async function handleSearch() {
    if (!date) {
      show("Pick a date first.");
      return;
    }

    setLoading(true);
    setResults([]);
    setVoucherLinks({});
    setRowProgress({});
    setWaReport(null);
    setRangeFrom("");
    setRangeTo("");

    try {
      const data = await apiJSON(`/birthdays?date=${encodeURIComponent(date)}`);
      const rows = Array.isArray(data) ? data : data?.contacts || [];
      setResults(rows);
      show(`Loaded ${rows.length} contacts.`);
    } catch (e) {
      console.error(e);
      show(`Failed to load birthdays: ${e?.message || e}`);
    } finally {
      setLoading(false);
    }
  }

  async function fetchVoucherLinkFor(c) {
    const name = safeFileName(c?.name || c?.fullname || "");
    const phone = digitsOnly(c?.phones || c?.phone || "");
    const key = `${date}|${name}|${phone}`;

    if (voucherLinks[key]) return voucherLinks[key];

    try {
      setProgress(key, {
        status: "GETTING_VOUCHER",
        message: "Getting voucher link...",
      });

      const qs = new URLSearchParams({ date, name, phone });
      const res = await fetch(`${API}/voucher/link?${qs.toString()}`);
      const text = await res.text();

      if (!res.ok) throw new Error(text || `HTTP ${res.status}`);

      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {}

      const out = {
        page_url: data?.page_url || "",
        view_url: data?.view_url || "",
      };

      setVoucherLinks((m) => ({ ...m, [key]: out }));
      setProgress(key, { status: "VOUCHER_READY", message: "Voucher ready" });

      return out;
    } catch (e) {
      const out = {
        error: e?.message || String(e),
        page_url: "",
        view_url: "",
      };

      setVoucherLinks((m) => ({ ...m, [key]: out }));
      setProgress(key, { status: "VOUCHER_FAILED", message: out.error });

      return out;
    }
  }

  async function getVoucherLinksForRows(rows) {
    if (!rows.length) {
      show("No contacts selected.");
      return {};
    }

    setLinkLoading(true);
    const finalLinks = {};

    try {
      for (const c of rows) {
        const key = linkKey(c);
        const link = await fetchVoucherLinkFor(c);
        finalLinks[key] = link || {};
      }

      show(`Voucher links requested for ${rows.length} selected contacts.`);
      return finalLinks;
    } finally {
      setLinkLoading(false);
    }
  }

  function buildWaPayload({ to, name, image, activeBranch }) {
    return {
      template: TEMPLATE_NAME,
      template_name: TEMPLATE_NAME,
      lang: LANG,
      language: LANG,
      requested_limit: 1,
      limit: 1,
      filter_emoji_names: false,
      filter_wrong_number_names: false,
      branch: activeBranch || "",
      items: [
        {
          to,
          phone: to,
          name,
          image,
          image_url: image,
          header_image: image,
          branch: activeBranch || "",
        },
      ],
    };
  }

  async function sendSelectedContactsWithVoucherLinks() {
    if (!date) {
      show("Pick a date first.");
      return;
    }

    if (!results.length) {
      show("No contacts loaded.");
      return;
    }

    const rowsToSend = getSelectedRows();

    if (!rowsToSend.length) {
      show("No contacts selected. Check From / To numbers.");
      return;
    }

    setWaBusy(true);
    setWaReport(null);

    const activeBranch =
      typeof getActiveBranch === "function" ? getActiveBranch() : "";

    const reportRows = [];
    let sent = 0;
    let failed = 0;
    let notWhatsapp = 0;
    let skippedInvalid = 0;

    try {
      rowsToSend.forEach((c) => {
        const key = linkKey(c);
        setProgress(key, {
          status: "WAITING",
          message: "Waiting...",
          wamid: "",
          wa_error_code: "",
        });
      });

      show(`Sending ${rowsToSend.length} selected contacts one by one...`);

      for (let i = 0; i < rowsToSend.length; i++) {
        const c = rowsToSend[i];
        const key = linkKey(c);

        const to = digitsOnly(c?.phones || c?.phone || "");
        const name = cleanName(c?.name || c?.fullname || "");

        if (!to) {
          skippedInvalid += 1;

          const row = {
            to,
            name,
            status: "SKIPPED_INVALID",
            wa_error_code: "NO_PHONE",
            wamid: "",
          };

          reportRows.push(row);

          setProgress(key, {
            status: "SKIPPED_INVALID",
            message: "Skipped: no phone",
            wa_error_code: "NO_PHONE",
          });

          continue;
        }

        setProgress(key, {
          status: "GETTING_VOUCHER",
          message: `Getting voucher ${i + 1}/${rowsToSend.length}`,
        });

        const link = await fetchVoucherLinkFor(c);
        const imageToSend = link?.view_url || DEFAULT_IMG;

        setProgress(key, {
          status: "SENDING",
          message: `Sending ${i + 1}/${rowsToSend.length}`,
        });

        try {
          const payload = buildWaPayload({
            to,
            name,
            image: imageToSend,
            activeBranch,
          });

          const res = await safePostJSON("/wa360/send-batch", payload);

          const first = Array.isArray(res?.results) ? res.results[0] : null;

          const status =
            first?.status ||
            first?.result ||
            (res?.sent || res?.success ? "SENT" : "FAILED");

          if (status === "SENT") sent += 1;
          else if (status === "NOT_WHATSAPP") notWhatsapp += 1;
          else failed += 1;

          const row = {
            to,
            name,
            status,
            wa_error_code:
              first?.wa_error_code ||
              first?.error_code ||
              first?.error ||
              res?.error ||
              "",
            wamid:
              first?.wamid ||
              first?.message_id ||
              first?.id ||
              res?.wamid ||
              "",
          };

          reportRows.push(row);

          setProgress(key, {
            status,
            message:
              status === "SENT"
                ? "Sent"
                : status === "NOT_WHATSAPP"
                ? "Not WhatsApp"
                : "Failed",
            wa_error_code: row.wa_error_code,
            wamid: row.wamid,
          });

          setWaReport({
            total_considered: rowsToSend.length,
            sent,
            failed,
            not_whatsapp: notWhatsapp,
            skipped_invalid: skippedInvalid,
            results: [...reportRows],
          });
        } catch (e) {
          failed += 1;

          const row = {
            to,
            name,
            status: "FAILED",
            wa_error_code: e?.message || String(e),
            wamid: "",
            error: e?.message || String(e),
          };

          reportRows.push(row);

          setProgress(key, {
            status: "FAILED",
            message: e?.message || String(e),
            wa_error_code: e?.message || String(e),
          });

          setWaReport({
            total_considered: rowsToSend.length,
            sent,
            failed,
            not_whatsapp: notWhatsapp,
            skipped_invalid: skippedInvalid,
            results: [...reportRows],
          });
        }
      }

      show(
        `Done. Sent: ${sent}, Failed: ${failed}, Not WhatsApp: ${notWhatsapp}, Skipped: ${skippedInvalid}`
      );
    } finally {
      setWaBusy(false);
    }
  }

  function exportFile(type) {
    if (!date) {
      show("Pick a date first.");
      return;
    }

    window.location.href = apiUrl(
      `/birthdays/export-${type}?date=${encodeURIComponent(date)}`
    );
  }

  function selectAll() {
    setRangeFrom("1");
    setRangeTo(String(results.length));
  }

  function selectFromCurrentToEnd() {
    if (!rangeFrom) {
      show("Enter From number first, example 251.");
      return;
    }

    setRangeTo("");
  }

  function statusStyle(status) {
    if (status === "SENT") return { color: "#047857", label: "Sent" };
    if (status === "SENDING")
      return { color: "#2563eb", label: "Sending..." };
    if (status === "GETTING_VOUCHER")
      return { color: "#2563eb", label: "Getting voucher..." };
    if (status === "VOUCHER_READY")
      return { color: "#047857", label: "Voucher ready" };
    if (status === "NOT_WHATSAPP")
      return { color: "#b45309", label: "Not WhatsApp" };
    if (status === "SKIPPED_INVALID")
      return { color: "#b45309", label: "Skipped" };
    if (status === "FAILED" || status === "VOUCHER_FAILED")
      return { color: "#b91c1c", label: "Failed" };
    if (status === "WAITING") return { color: "#6b7280", label: "Waiting" };
    return { color: "#6b7280", label: "-" };
  }

  const summary = useMemo(() => {
    if (!waReport) return null;

    return {
      total: Number(waReport.total_considered || 0),
      sent: Number(waReport.sent || 0),
      failed: Number(waReport.failed || 0),
      notWhatsapp: Number(waReport.not_whatsapp || 0),
      skippedInvalid: Number(waReport.skipped_invalid || 0),
    };
  }, [waReport]);

  return (
    <div style={s.wrap}>
      <div style={s.card}>
        <h2 style={{ margin: 0, marginBottom: 6 }}>
          🎂 Birthday Voucher Sender
        </h2>

        <div style={{ color: C.dim }}>
          Select birthday date, choose row range, then send only selected
          contacts. Progress appears on each table row.
        </div>
      </div>

      {notice && (
        <div style={{ ...s.card, background: "rgba(59,130,246,0.12)" }}>
          <b>{notice}</b>
        </div>
      )}

      <div style={s.card}>
        <div style={s.row}>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={s.input}
          />

          <button
            style={s.btn}
            onClick={handleSearch}
            disabled={loading || waBusy || linkLoading}
          >
            {loading ? "Searching..." : "Search Date"}
          </button>

          <button
            style={s.btn}
            onClick={() => exportFile("csv")}
            disabled={!results.length}
          >
            Export CSV
          </button>

          <button
            style={s.btn}
            onClick={() => exportFile("vcf")}
            disabled={!results.length}
          >
            Export VCF
          </button>
        </div>
      </div>

      <div style={s.card}>
        <h3 style={{ marginTop: 0 }}>Send Selection</h3>

        <div style={s.row}>
          <div>
            <div style={{ color: C.dim, fontSize: 13 }}>From row</div>
            <input
              type="number"
              min="1"
              placeholder="251"
              value={rangeFrom}
              onChange={(e) => setRangeFrom(e.target.value)}
              style={s.smallInput}
            />
          </div>

          <div>
            <div style={{ color: C.dim, fontSize: 13 }}>To row</div>
            <input
              type="number"
              min="1"
              placeholder="Empty = end"
              value={rangeTo}
              onChange={(e) => setRangeTo(e.target.value)}
              style={s.smallInput}
            />
          </div>

          <button
            style={s.btn}
            onClick={selectAll}
            disabled={!results.length || waBusy || linkLoading}
          >
            Select All
          </button>

          <button
            style={s.btn}
            onClick={selectFromCurrentToEnd}
            disabled={!results.length || waBusy || linkLoading}
          >
            From Number Till End
          </button>

          <button
            style={s.btn}
            onClick={() => getVoucherLinksForRows(selectedRows)}
            disabled={!selectedRows.length || waBusy || linkLoading}
          >
            {linkLoading ? "Getting Links..." : `Get Links (${selectedRows.length})`}
          </button>

          <button
            style={s.btnGreen}
            onClick={sendSelectedContactsWithVoucherLinks}
            disabled={!selectedRows.length || waBusy || linkLoading}
          >
            {waBusy ? "Sending..." : `Send Selected (${selectedRows.length})`}
          </button>
        </div>

        <div style={{ marginTop: 12, color: C.dim }}>
          Example: From <b style={{ color: C.text }}>251</b>, To empty = send
          from 251 till the end.
        </div>
      </div>

      <div style={s.card}>
        <div style={s.row}>
          <div>
            <div style={{ color: C.dim, fontSize: 13 }}>Selected Date</div>
            <b>{date || "-"}</b>
          </div>

          <div>
            <div style={{ color: C.dim, fontSize: 13 }}>Loaded Contacts</div>
            <b>{results.length}</b>
          </div>

          <div>
            <div style={{ color: C.dim, fontSize: 13 }}>Selected To Send</div>
            <b style={{ color: C.green }}>{selectedRows.length}</b>
          </div>

          <div>
            <div style={{ color: C.dim, fontSize: 13 }}>Template</div>
            <b>{TEMPLATE_NAME}</b>
          </div>

          {summary && (
            <>
              <div>
                <div style={{ color: C.dim, fontSize: 13 }}>Sent</div>
                <b style={{ color: C.green }}>{summary.sent}</b>
              </div>

              <div>
                <div style={{ color: C.dim, fontSize: 13 }}>Failed</div>
                <b style={{ color: C.red }}>{summary.failed}</b>
              </div>

              <div>
                <div style={{ color: C.dim, fontSize: 13 }}>Not WhatsApp</div>
                <b style={{ color: C.yellow }}>{summary.notWhatsapp}</b>
              </div>

              <div>
                <div style={{ color: C.dim, fontSize: 13 }}>Skipped</div>
                <b style={{ color: C.yellow }}>{summary.skippedInvalid}</b>
              </div>
            </>
          )}
        </div>
      </div>

      <div style={s.card}>
        <h3 style={{ marginTop: 0 }}>Birthday Contact List</h3>

        {!loading && results.length === 0 && (
          <div style={{ color: C.dim }}>No contacts loaded.</div>
        )}

        {results.length > 0 && (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>#</th>
                  <th style={s.th}>Selected</th>
                  <th style={s.th}>Progress</th>
                  <th style={s.th}>Name</th>
                  <th style={s.th}>Phone</th>
                  <th style={s.th}>Birthday</th>
                  <th style={s.th}>Voucher Link</th>
                  <th style={s.th}>Header Image Used</th>
                </tr>
              </thead>

              <tbody>
                {results.map((c, i) => {
                  const key = linkKey(c);
                  const link = voucherLinks[key];
                  const progress = rowProgress[key] || {};
                  const st = statusStyle(progress.status);
                  const selected = isRowSelected(i);
                  const cellStyle = selected
                    ? s.selectedTd
                    : i % 2 === 0
                    ? s.td
                    : s.tdAlt;

                  return (
                    <tr key={i}>
                      <td style={{ ...cellStyle, fontWeight: 800 }}>
                        {i + 1}
                      </td>

                      <td style={cellStyle}>
                        {selected ? (
                          <b style={{ color: "#047857" }}>YES</b>
                        ) : (
                          <span style={{ color: "#6b7280" }}>NO</span>
                        )}
                      </td>

                      <td style={cellStyle}>
                        <b style={{ color: st.color }}>{st.label}</b>

                        {progress.message && (
                          <div
                            style={{
                              fontSize: 12,
                              color: "#374151",
                              marginTop: 4,
                              maxWidth: 260,
                              wordBreak: "break-word",
                            }}
                          >
                            {progress.message}
                          </div>
                        )}

                        {progress.wa_error_code && (
                          <div
                            style={{
                              fontSize: 12,
                              color: "#991b1b",
                              marginTop: 4,
                              maxWidth: 260,
                              wordBreak: "break-word",
                            }}
                          >
                            Error: {progress.wa_error_code}
                          </div>
                        )}
                      </td>

                      <td style={cellStyle}>{c?.name || c?.fullname || "-"}</td>
                      <td style={cellStyle}>{c?.phones || c?.phone || "-"}</td>
                      <td style={cellStyle}>{c?.birthday || "-"}</td>

                      <td style={cellStyle}>
                        {link?.error ? (
                          <span style={{ color: "#b91c1c", fontWeight: 700 }}>
                            {link.error}
                          </span>
                        ) : link?.page_url ? (
                          <div>
                            <button
                              style={{
                                ...s.btn,
                                color: "#ffffff",
                                background: C.blue,
                              }}
                              onClick={() => window.open(link.page_url, "_blank")}
                            >
                              Open
                            </button>
                            <div style={s.linkText}>{link.page_url}</div>
                          </div>
                        ) : (
                          <button
                            style={{
                              ...s.btn,
                              color: "#ffffff",
                              background: C.blue,
                            }}
                            onClick={() => fetchVoucherLinkFor(c)}
                            disabled={waBusy}
                          >
                            Get Link
                          </button>
                        )}
                      </td>

                      <td style={cellStyle}>
                        <div style={s.linkText}>
                          {link?.view_url || DEFAULT_IMG}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {waReport?.results?.length > 0 && (
        <div style={s.card}>
          <h3 style={{ marginTop: 0 }}>Send Report</h3>

          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>#</th>
                  <th style={s.th}>To</th>
                  <th style={s.th}>Name</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>Error Code</th>
                  <th style={s.th}>WAMID</th>
                </tr>
              </thead>

              <tbody>
                {waReport.results.map((r, i) => {
                  const cellStyle = i % 2 === 0 ? s.td : s.tdAlt;

                  return (
                    <tr key={i}>
                      <td style={{ ...cellStyle, fontWeight: 800 }}>
                        {i + 1}
                      </td>

                      <td style={cellStyle}>{r.to || "-"}</td>
                      <td style={cellStyle}>{r.name || "-"}</td>

                      <td style={cellStyle}>
                        <b
                          style={{
                            color:
                              r.status === "SENT"
                                ? "#047857"
                                : r.status === "NOT_WHATSAPP"
                                ? "#b45309"
                                : "#b91c1c",
                          }}
                        >
                          {r.status || "-"}
                        </b>
                      </td>

                      <td style={cellStyle}>{r.wa_error_code || "-"}</td>

                      <td style={cellStyle}>
                        {r.wamid ? `${String(r.wamid).slice(0, 28)}...` : "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}